/**
 * Versiegeschiedenis van het offerteconcept (Joris, 4 sep 2026). Elke AI-wijziging,
 * Gripp-laadactie en Gripp-update legt de stand vóór die actie vast; hier kun je
 * per versie alles, alleen de omschrijving of alleen de regels terugzetten.
 * Module-niveau component (geen remount bij elke toets in de parent).
 */
import React from 'react';
import { Box, Button, Chip, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';

const FONT = '"Inter", system-ui, sans-serif';
const NAV_FONT = '"Manrope", system-ui, sans-serif';

const totaal = (lines) => (lines || []).reduce((s, l) => s + (Number(l.amount ?? 1) || 0) * (Number(l.sellingprice) || 0), 0);
const euro = (n) => `€ ${Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tijd = (iso) => {
  const d = new Date(iso); const nu = new Date();
  const zelfdeDag = d.toDateString() === nu.toDateString();
  return `${zelfdeDag ? 'vandaag' : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
};
const regelsGelijk = (a, b) => JSON.stringify((a || []).map((l) => [l.description || l.product, l.amount, l.sellingprice, l.section]))
  === JSON.stringify((b || []).map((l) => [l.description || l.product, l.amount, l.sellingprice, l.section]));

export default function VersiesDrawer({ open, onClose, versies = [], huidig, onHerstel }) {
  const lijst = [...versies].reverse();
  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: 440, maxWidth: '100vw' } } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
        <HistoryIcon sx={{ color: '#64748b' }} />
        <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 15, flex: 1 }}>Versies van dit concept</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Sluiten"><CloseIcon fontSize="small" /></IconButton>
      </Stack>
      <Box sx={{ px: 2, py: 1.5, overflowY: 'auto' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', mb: 1.5 }}>
          Vastgelegd vóór elke AI-wijziging, bij laden uit Gripp en vóór bijwerken in Gripp. Herstellen zet de huidige stand zelf ook als versie weg, dus je kunt altijd terug.
        </Typography>
        {huidig && (
          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #c7d2fe', bgcolor: '#eef2ff', mb: 1.5 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 800, color: '#3730a3' }}>Huidige stand</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#475569' }}>
              {(huidig.lines || []).length} regels · {euro(totaal(huidig.lines))} · omschrijving {String(huidig.description || '').length.toLocaleString('nl-NL')} tekens
            </Typography>
          </Box>
        )}
        {lijst.length === 0 && (
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8', textAlign: 'center', py: 4 }}>Nog geen eerdere versies in dit gesprek.</Typography>
        )}
        {lijst.map((v, i) => {
          const zelfdeRegels = huidig ? regelsGelijk(v.lines, huidig.lines) : false;
          const zelfdeOmschrijving = huidig ? String(v.description || '') === String(huidig.description || '') : false;
          return (
            <Box key={v.id || i} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', mb: 1.25, bgcolor: '#fff' }}>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 800, color: '#0f172a', flex: 1 }}>{v.label || 'Versie'}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{tijd(v.at)}</Typography>
              </Stack>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#475569', mt: 0.25 }}>
                {(v.lines || []).length} regels · {euro(totaal(v.lines))} · omschrijving {String(v.description || '').length.toLocaleString('nl-NL')} tekens
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}>
                <Chip size="small" label={zelfdeRegels ? 'regels gelijk aan nu' : 'regels anders dan nu'}
                  sx={{ height: 20, fontSize: 10.5, bgcolor: zelfdeRegels ? '#f1f5f9' : '#fef3c7', color: zelfdeRegels ? '#64748b' : '#92400e' }} />
                <Chip size="small" label={zelfdeOmschrijving ? 'omschrijving gelijk aan nu' : 'omschrijving anders dan nu'}
                  sx={{ height: 20, fontSize: 10.5, bgcolor: zelfdeOmschrijving ? '#f1f5f9' : '#fef3c7', color: zelfdeOmschrijving ? '#64748b' : '#92400e' }} />
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="contained" onClick={() => onHerstel(v, 'alles')} disabled={zelfdeRegels && zelfdeOmschrijving}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' } }}>Herstel alles</Button>
                <Tooltip title="Alleen de omschrijving van deze versie terugzetten; de huidige regels blijven staan">
                  <span><Button size="small" variant="outlined" onClick={() => onHerstel(v, 'omschrijving')} disabled={zelfdeOmschrijving}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>Alleen omschrijving</Button></span>
                </Tooltip>
                <Tooltip title="Alleen de offerteregels van deze versie terugzetten; de huidige omschrijving blijft staan">
                  <span><Button size="small" variant="outlined" onClick={() => onHerstel(v, 'regels')} disabled={zelfdeRegels}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>Alleen regels</Button></span>
                </Tooltip>
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Drawer>
  );
}
